/**
 * Email User Timeline Sidebar Component
 *
 * LEFT panel for Steps 3 & 4 (email detail & compose views).
 * Shows user info header + email timeline list.
 *
 * Layout:
 * - User header (avatar, name, email count, menu button)
 * - Timeline list of emails (expandable previews)
 * - Back button to return to conversation list
 *
 * Props:
 * - conversation: Current email conversation
 * - messages: List of emails in the conversation
 * - selectedMessageId: Currently selected email ID
 * - onSelectMessage: Callback when email is clicked
 * - onBack: Callback to return to conversation list
 */

import React, { useState } from "react";
import { EmailMessage, EmailConversation } from "./types";
import { FaChevronDown, FaChevronRight, FaEllipsisH } from "react-icons/fa";

interface EmailUserTimelineSidebarProps {
  conversation: EmailConversation;
  messages: EmailMessage[];
  selectedMessageId?: string;
  onSelectMessage: (message: EmailMessage) => void;
  onBack: () => void;
}

interface TimelineItemProps {
  message: EmailMessage;
  isSelected: boolean;
  isLast: boolean;
  onClick: () => void;
}

/**
 * Timeline Item - Compact email in sidebar
 */
const TimelineItem: React.FC<TimelineItemProps> = ({
  message,
  isSelected,
  isLast,
  onClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isOutbound = message.direction === "outbound";

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString([], {
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Strip HTML for preview
  const bodyPreview = message.body?.replace(/<[^>]*>/g, "").trim().slice(0, 60) || "";

  return (
    <div className="relative pl-6">
      {/* Timeline line */}
      {!isLast && (
        <div className="absolute left-[7px] top-[18px] w-[2px] h-[calc(100%-6px)] bg-indigo-200 dark:bg-indigo-500/30" />
      )}

      {/* Timeline bullet */}
      <div
        className={`absolute left-0 top-[6px] w-4 h-4 rounded-full border-2 border-white dark:border-gray-900 shadow-sm ${
          isOutbound ? "bg-green-500" : "bg-indigo-500"
        }`}
      />

      {/* Content */}
      <div
        className={`pb-4 cursor-pointer rounded-lg transition-colors px-2 py-1 ${
          isSelected ? "bg-indigo-50 dark:bg-indigo-500/10" : "hover:bg-slate-50 dark:hover:bg-gray-800"
        }`}
        onClick={onClick}
      >
        {/* Header row */}
        <div className="flex items-start gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="mt-1 p-0.5 text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300 transition-colors"
          >
            {isExpanded ? (
              <FaChevronDown size={10} />
            ) : (
              <FaChevronRight size={10} />
            )}
          </button>

          <div className="flex-1 min-w-0">
            {/* Subject */}
            <p className="text-sm font-semibold text-slate-800 dark:text-gray-100 truncate">
              {message.subject}
            </p>

            {/* From / To emails */}
            <div className="text-xs text-slate-500 dark:text-gray-400 mt-1">
              <p className="truncate">
                <span className="text-slate-400 dark:text-gray-500">From:</span> {message.from.name || message.from.email}
              </p>
              <p className="truncate">
                <span className="text-slate-400 dark:text-gray-500">To:</span> {message.to.map(t => t.name || t.email).join(", ")}
              </p>
            </div>

            {/* Date/Time */}
            <p className="text-xs text-slate-400 dark:text-gray-500 mt-1">
              {formatDate(message.sentAt)} · {formatTime(message.sentAt)}
            </p>

            {/* Preview (when collapsed) */}
            {!isExpanded && (
              <p className="text-xs text-slate-500 dark:text-gray-400 truncate mt-1">
                {bodyPreview}...
              </p>
            )}

            {/* Expanded content */}
            {isExpanded && (
              <div className="mt-2 text-xs text-slate-500 dark:text-gray-400 leading-relaxed">
                <p>{bodyPreview}...</p>
              </div>
            )}
          </div>

          {isOutbound && (
            <span className="text-[10px] bg-green-100 dark:bg-green-500/15 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
              Sent
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Email User Timeline Sidebar
 */
export const EmailUserTimelineSidebar: React.FC<EmailUserTimelineSidebarProps> = ({
  conversation,
  messages,
  selectedMessageId,
  onSelectMessage,
  onBack,
}) => {
  const recipient = conversation.participants.find(
    (p) => p.email !== "support@convoai.com"
  );

  const initials = recipient?.name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "??";

  return (
    <div className="w-[320px] min-w-[280px] bg-white dark:bg-gray-900 border-r border-[#e8ecf4] dark:border-gray-800 flex flex-col h-full overflow-hidden">
      {/* User Header */}
      <div className="border-b border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-base font-bold">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-slate-800 dark:text-gray-100 truncate">
              {recipient?.name || "Unknown"}
            </h2>
            <p className="text-xs text-slate-500 dark:text-gray-400">
              {messages.length} email{messages.length !== 1 ? "s" : ""}
            </p>
          </div>
          <button className="p-2 text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300 transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-gray-800">
            <FaEllipsisH size={14} />
          </button>
        </div>
      </div>

      {/* Timeline Content */}
      <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-gray-800/60 p-4">
        <div>
          {messages.map((message, index) => (
            <TimelineItem
              key={message.id}
              message={message}
              isSelected={selectedMessageId === message.id}
              isLast={index === messages.length - 1}
              onClick={() => onSelectMessage(message)}
            />
          ))}
        </div>
      </div>

      {/* Back button */}
      <div className="p-4 border-t border-slate-100 dark:border-gray-800 bg-white dark:bg-gray-900">
        <button
          onClick={onBack}
          className="w-full text-sm text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-gray-200 font-medium"
        >
          ← Back to all conversations
        </button>
      </div>
    </div>
  );
};
